// 영어 단어 검사 문항 생성 로직
// englishWordBank(레벨별 50단어)를 바탕으로 5개 유형 문항을 결정적으로 생성한다.
// - 뜻맞추기(meaning): 영단어를 보고 뜻 고르기 (50문항)
// - 뜻보고 영단어(reverse): 뜻을 보고 영단어 고르기 (50문항)
// - 유의어(synonym): 영단어의 유의어 고르기 (50문항)
// - 반의어(antonym): 영단어의 반의어 고르기 (50문항)
// - 빈칸 채우기(cloze): 문장 빈칸에 들어갈 영단어 고르기 (30문항)
// Math.random을 쓰지 않아 항상 같은 문항이 생성되므로, 진행 저장/복원이 안정적으로 동작한다.

import { englishWordBank, type EnglishLevel, type EnglishWord } from '@/mocks/englishWords';

export type EnglishQuestionType = 'meaning' | 'reverse' | 'synonym' | 'antonym' | 'cloze';

export interface EnglishQuestion {
  id: string;
  type: EnglishQuestionType;
  prompt: string;
  options: string[];
  answer: number;
  explanation: string;
}

export interface EnglishTypeMeta {
  id: EnglishQuestionType;
  label: string;
  icon: string;
  description: string;
}

export const englishTypeMeta: EnglishTypeMeta[] = [
  { id: 'meaning', label: '뜻 맞추기', icon: 'ri-translate-2', description: '영단어를 보고 알맞은 뜻을 고릅니다.' },
  { id: 'reverse', label: '뜻 보고 영단어', icon: 'ri-sparkling-line', description: '뜻을 보고 알맞은 영단어를 고릅니다.' },
  { id: 'synonym', label: '유의어 맞추기', icon: 'ri-link', description: '영단어와 뜻이 같은 유의어를 고릅니다.' },
  { id: 'antonym', label: '반의어 맞추기', icon: 'ri-contrast-2-line', description: '영단어와 뜻이 반대인 반의어를 고릅니다.' },
  { id: 'cloze', label: '문장 빈칸 채우기', icon: 'ri-edit-line', description: '문장의 빈칸에 들어갈 영단어를 고릅니다.' },
];

export const ENGLISH_TYPE_COUNT: Record<EnglishQuestionType, number> = {
  meaning: 50,
  reverse: 50,
  synonym: 50,
  antonym: 50,
  cloze: 30,
};

export const ENGLISH_TOTAL_QUESTIONS = Object.values(ENGLISH_TYPE_COUNT).reduce((a, b) => a + b, 0);

function pickPrompt(w: EnglishWord, type: EnglishQuestionType): string {
  switch (type) {
    case 'meaning':
    case 'synonym':
    case 'antonym':
      return w.word;
    case 'reverse':
      return w.meaning;
    case 'cloze':
      return w.sentence;
    default:
      return w.word;
  }
}

function pickCorrect(w: EnglishWord, type: EnglishQuestionType): string {
  switch (type) {
    case 'meaning':
      return w.meaning;
    case 'reverse':
      return w.word;
    case 'synonym':
      return w.synonym;
    case 'antonym':
      return w.antonym;
    case 'cloze':
      return w.word;
    default:
      return w.word;
  }
}

function pickDistractor(w: EnglishWord, type: EnglishQuestionType): string {
  switch (type) {
    case 'meaning':
      return w.meaning;
    case 'reverse':
    case 'cloze':
      return w.word;
    case 'synonym':
      return w.synonym;
    case 'antonym':
      return w.antonym;
    default:
      return w.word;
  }
}

function pickExplanation(w: EnglishWord, type: EnglishQuestionType): string {
  switch (type) {
    case 'meaning':
      return `'${w.word}'의 뜻은 '${w.meaning}'입니다.`;
    case 'reverse':
      return `'${w.meaning}'를 뜻하는 영단어는 '${w.word}'입니다.`;
    case 'synonym':
      return `'${w.word}'와(과) 뜻이 같은 유의어는 '${w.synonym}'입니다.`;
    case 'antonym':
      return `'${w.word}'와(과) 뜻이 반대인 반의어는 '${w.antonym}'입니다.`;
    case 'cloze':
      return `빈칸에 들어갈 단어는 '${w.word}'(${w.meaning})입니다.`;
    default:
      return '';
  }
}

function buildType(
  level: EnglishLevel,
  type: EnglishQuestionType,
  count: number,
  idOffset: number,
  wordStart: number,
): EnglishQuestion[] {
  const bank = englishWordBank[level];
  const questions: EnglishQuestion[] = [];

  for (let i = 0; i < count; i++) {
    const word = bank[wordStart + i];
    const correct = pickCorrect(word, type);

    // 오답 3개를 같은 유형의 인접한 다른 단어에서 추출
    // (정답과 중복되거나, 문제의 단어 자체와 같으면 정답 유출이 되므로 다음 단어로 넘어감)
    const distractors: string[] = [];
    let step = 1;
    while (distractors.length < 3 && step < count) {
      const d = pickDistractor(bank[wordStart + ((i + step) % count)], type);
      if (d !== correct && d !== word.word && !distractors.includes(d)) {
        distractors.push(d);
      }
      step += 1;
    }

    // 정답 위치를 문항마다 순환 배치
    const pos = i % 4;
    const options = [...distractors];
    options.splice(pos, 0, correct);

    questions.push({
      id: `${type}-${idOffset + i}`,
      type,
      prompt: pickPrompt(word, type),
      options,
      answer: pos,
      explanation: pickExplanation(word, type),
    });
  }

  return questions;
}

export function buildEnglishQuestions(level: EnglishLevel): EnglishQuestion[] {
  const order: EnglishQuestionType[] = ['meaning', 'reverse', 'synonym', 'antonym', 'cloze'];
  const questions: EnglishQuestion[] = [];
  let offset = 0;
  let wordStart = 0;

  order.forEach((type) => {
    const count = ENGLISH_TYPE_COUNT[type];
    questions.push(...buildType(level, type, count, offset, wordStart));
    offset += count;
    wordStart += count;
  });

  return questions;
}